/**
 * Pure utilities shared across web and mobile.
 * cn, LayoutProps, fileToBase64 stay in each app (Tailwind/DOM-specific).
 */

export function throttle<T extends () => void>(fn: T, ms: number) {
  let lastTime = 0;
  return () => {
    const now = Date.now();
    if (now - lastTime >= ms) {
      fn();
      lastTime = now;
    }
  };
}

/**
 * createRequestGate limits how many `run` callbacks execute concurrently.
 * Waiters are released in FIFO order, so work starts roughly in the order it
 * was queued.
 *
 * Over HTTP/2 the browser happily runs hundreds of requests in parallel — the
 * old ~6-per-origin limit does not apply — and each one costs the backend
 * database connections. Callers that fan a list out into one request per item
 * put the gate in front.
 *
 * The slot is released in a `finally`: a rejected or aborted run frees its slot
 * exactly like a successful one. Releasing only on success would leak a slot
 * per failure and, after `limit` failures, wedge everything behind it forever.
 */
export function createRequestGate(limit: number) {
  let active = 0;
  const waiting: Array<() => void> = [];

  return async function withSlot<T>(run: () => Promise<T>): Promise<T> {
    if (active >= limit) {
      await new Promise<void>((resolve) => waiting.push(resolve));
    }
    active += 1;
    try {
      return await run();
    } finally {
      active -= 1;
      waiting.shift()?.();
    }
  };
}

export const formModes = ["create", "update"] as const;
export type FormMode = (typeof formModes)[number];

export type Nullable<T> = T | null | undefined;

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;

  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

export function getFirstWord(str: string): string {
  return str.split(" ")[0];
}

export function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

export type Primitive =
  | number
  | string
  | boolean
  | bigint
  | symbol
  | null
  | undefined;
